import { Box, Button, InputAdornment, Typography } from '@material-ui/core';
import {
  Backdrop,
  EmailIconTwoTone,
  Input,
  InputPassword,
  PasswordIconTwoTone,
} from '@room-match/shared-ui-components';
import * as Sentry from '@sentry/react';
import { FrontLayout } from 'components';
import { FormikHelpers, FormikProps, useFormik } from 'formik';
import { parse } from 'query-string';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { COLORS } from 'shared/constants/COLORS';
import { ROUTES } from 'shared/constants/ROUTES';
import { talentDao } from 'shared/dao/talentDao';
import { Checkbox } from 'shared/elements';
import { ISignInRequestPayload } from 'shared/interfaces/IUser';
import { setTheme } from 'shared/redux/slicers/theme.slicer';
import { autoLogin, selectUserState, setUser, userLogin } from 'shared/redux/slicers/user.slicer';
import { getErrorMessage } from 'shared/utils/getErrorMessage';
import { ls } from 'shared/utils/ls';
import * as yup from 'yup';
import { useStyles } from './Login.styles';
const { PRIMARY_TEXT } = COLORS;

const { getMe } = talentDao();

const Login = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isAutoLoginLoading, setIsAutoLoginLoading] = useState<boolean>(false);
  const { isLoading: loading, isLoggedIn } = useSelector(selectUserState);
  const [isRememberMe, setIsRememberMe] = useState<boolean>(false);
  const { setLS, getLS, removeLS } = ls();
  const option = getLS('option');
  const projectId = getLS('projectId');
  const projectName = getLS('project_name');
  const completeCheckin = getLS('complete_checkin');
  const completeST = getLS('complete_st');
  const [expand, setexpand] = useState(false);
  const asyncDispatchAutoLogin = (token: string) => Promise.resolve(dispatch(autoLogin(token)));
  const usernameField: any = React.useRef(null);

  useEffect(() => {
    dispatch(setTheme('dark'));
    setIsAutoLoginLoading(true);
    const queryParams = location.search;
    if (queryParams) {
      const token = parse(queryParams)?.token as string;
      console.log({ token });

      if (token) {
        (async (token: string) => {
          await asyncDispatchAutoLogin(token);
          setIsAutoLoginLoading(false);
        })(token);
      } else {
        setIsAutoLoginLoading(false);
      }
    } else {
      setIsAutoLoginLoading(false);
    }

    return () => setIsAutoLoginLoading(false);
  }, []);

  const isLoading = loading || isAutoLoginLoading;

  useEffect(() => {
    const isCompleted = getLS('is_completed_primary_details');
    if (isLoggedIn) {
      if (isCompleted) {
        if (option && projectId) {
          removeLS('option');
          removeLS('projectId');
          history.push(`/talent/${option}/${projectId}`);
        } else {
          //handle complete checkin
          if (completeCheckin) {
            const { projectId, sessionId } = JSON.parse(completeCheckin);
            removeLS('complete_checkin');
            history.push(`${ROUTES.TALENT.COMPLETE_CHECKIN}/${projectId}/${sessionId}`);
          } else if (completeST) {
            const { projectId, roleId } = JSON.parse(completeST);
            removeLS('complete_st');
            history.push(`${ROUTES.TALENT.COMPLETE_ST}/${projectId}/${roleId}`);
          } else {
            history.push(ROUTES.TALENT.DASHBOARD);
          }
        }
      } else {
        history.push(ROUTES.REGISTRATION);
      }
    }
  }, [isLoggedIn]);

  const initialValues: ISignInRequestPayload = {
    user: {
      email: '',
      password: '',
      platform: 'web',
      device: window.navigator.userAgent,
    },
  };

  const signInValidationSchema: yup.SchemaOf<ISignInRequestPayload> = yup.object().shape({
    user: yup.object({
      email: yup.string().email('Please enter a valid email address').trim().required('Email is required'),
      password: yup.string().trim().required('Password is required'),
      platform: yup.string().required(''),
      device: yup.string().required(''),
    }),
  });

  const asyncLogin = (values: ISignInRequestPayload, isRememberMe?: boolean): Promise<any> =>
    Promise.resolve(dispatch(userLogin(values, isRememberMe)));

  const handleLoginSubmit = async (values: ISignInRequestPayload, helper: FormikHelpers<ISignInRequestPayload>) => {
    const response = await asyncLogin(values, isRememberMe);

    if (response) {
      const { data: user } = await getMe();
      helper.resetForm();
      Sentry.getCurrentScope().setUser({
        id: response.id,
        email: response.attributes.email,
        username: response.attributes.full_name,
      });
      dispatch(setUser({ user }));
    }
  };

  const form: FormikProps<ISignInRequestPayload> = useFormik({
    initialValues,
    validationSchema: signInValidationSchema,
    onSubmit: (values, helper) => handleLoginSubmit(values, helper),
  });

  const handleLogin = () => {};

  const handleInputKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  // event listener for when the user refereshes the page remve projectId and project_name from local storage
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      console.log('removing projectId and project_name from local storage');
      removeLS('projectId');
      removeLS('project_name');
      removeLS('option');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  //different layout from QR
  if (option && projectId)
    return (
      <FrontLayout type="login" containerWidth={390} newTalent>
        {projectName ? (
          <Typography variant="h5" style={{ textAlign: 'center', marginBottom: 25 }}>
            {option === 'check-in' ? 'Checkin' : 'Self Tapes'} for {projectName} Audition
          </Typography>
        ) : (
          <React.Fragment />
        )}
        <div className={classes.loginHead}>
          <Button
            disableElevation
            fullWidth
            style={{ marginTop: '20px' }}
            className={`${classes.registerBtn} ${classes.buttonCls}`}
            onClick={() => history.push('/signup')}
            disabled={isLoading}
          >
            Create your Profile
          </Button>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <hr style={{ width: '15%', height: 2, color: COLORS.PRIMARY }} />
          <span style={{ margin: 30, fontSize: 16 }}>OR</span>
          <hr style={{ width: '15%', height: 2, color: COLORS.PRIMARY }} />
        </div>
        <Box className={classes.boxContainer} component="div" style={{ height: expand ? 220 : 0 }}>
          <Input
            inputRef={usernameField}
            style={{ marginTop: 0 }}
            autoComplete="off"
            className={classes.input}
            name="user.email"
            variant="outlined"
            placeholder="Enter Email..."
            errorMessage={getErrorMessage(form.touched.user?.email, form.errors.user?.email)}
            fullWidth
            margin={'normal'}
            autoFocus
            inputProps={{ 'data-cy': 'email', tabIndex: 1 }}
            onChange={form.handleChange}
            onKeyPress={handleInputKeyPress}
            onKeyUp={(e) => {
              if (e.key.toLocaleLowerCase() === 'enter') {
                form.handleSubmit();
              }
            }}
            InputProps={{
              tabIndex: 1,
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIconTwoTone className={classes.startIcon} />
                </InputAdornment>
              ),
            }}
            value={form.values.user.email}
          />
          <InputPassword
            autoComplete="off"
            className={classes.input}
            fullWidth
            errorMessage={getErrorMessage(form.touched.user?.password, form.errors.user?.password)}
            name="user.password"
            variant="outlined"
            placeholder="Enter Password..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PasswordIconTwoTone className={classes.startIcon} />
                </InputAdornment>
              ),
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-cy': 'password', tabIndex: 2 }}
            onChange={form.handleChange}
            value={form.values.user.password}
            onKeyPress={handleInputKeyPress}
            style={{ marginTop: '15px' }}
            onKeyUp={(e) => {
              if (e.key.toLocaleLowerCase() === 'enter') {
                form.handleSubmit();
              }
            }}
          />
          <Box component="div" className={classes.extraContainer}>
            <Box component="div" className={classes.checkboxContainer}>
              <Checkbox
                value={isRememberMe}
                onChange={(e) => setIsRememberMe(e.target.checked)}
                label="Remember Me"
                name="rememberMe"
                labelPlacement="end"
              />
            </Box>
            <Box component="div">
              <Link to="/forgot_password" className={classes.link}>
                Forgot Password?
              </Link>
            </Box>
          </Box>
        </Box>
        <Box component="div">
          <Button
            color="primary"
            variant="contained"
            disableElevation
            fullWidth
            style={{ fontSize: 16 }}
            className={classes.buttonCls}
            onClick={() => {
              if (expand) {
                form.handleSubmit();
              } else {
                usernameField.current.focus();
                setexpand(true);
              }
            }}
            disabled={isLoading}
          >
            {isLoading ? 'Logging In..' : 'Log In'}
          </Button>
        </Box>
        {projectName && option === 'check-in' ? (
          <React.Fragment>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <hr style={{ width: '15%', height: 2, color: PRIMARY_TEXT }} />
              <span style={{ margin: 30, fontSize: 16 }}>OR</span>
              <hr style={{ width: '15%', height: 2, color: PRIMARY_TEXT }} />
            </div>
            <Typography style={{ textAlign: 'center', marginBottom: 25 }}>
              Account managed by your Agency / you don’t have access
            </Typography>
            <Box component="div">
              <Button
                color="primary"
                variant="contained"
                disableElevation
                fullWidth
                style={{ marginTop: '5px', fontSize: 16 }}
                className={classes.buttonCls}
                onClick={() => {
                  history.push(ROUTES.UNAUTHENTICATED);
                }}
                disabled={isLoading}
              >
                Verify Audition
              </Button>
            </Box>
          </React.Fragment>
        ) : (
          <React.Fragment />
        )}
        <Box component="div"></Box>
        <Backdrop isLoading={isLoading} />
      </FrontLayout>
    );

  return (
    <FrontLayout type="login" containerWidth={390} newTalent>
      <Box className={classes.boxContainer} component="div">
        <Input
          autoComplete="off"
          className={classes.input}
          name="user.email"
          variant="outlined"
          placeholder="Enter Email..."
          errorMessage={getErrorMessage(form.touched.user?.email, form.errors.user?.email)}
          fullWidth
          margin={'normal'}
          autoFocus
          inputProps={{ 'data-cy': 'email', tabIndex: 1 }}
          onChange={form.handleChange}
          onKeyPress={handleInputKeyPress}
          onKeyUp={(e) => {
            if (e.key.toLocaleLowerCase() === 'enter') {
              form.handleSubmit();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIconTwoTone className={classes.startIcon} />
              </InputAdornment>
            ),
          }}
          value={form.values.user.email}
        />
        <InputPassword
          autoComplete="off"
          className={classes.input}
          fullWidth
          errorMessage={getErrorMessage(form.touched.user?.password, form.errors.user?.password)}
          name="user.password"
          variant="outlined"
          placeholder="Enter Password..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PasswordIconTwoTone className={classes.startIcon} />
              </InputAdornment>
            ),
          }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'data-cy': 'password', tabIndex: 2 }}
          onChange={form.handleChange}
          value={form.values.user.password}
          onKeyPress={handleInputKeyPress}
          style={{ marginTop: '15px' }}
          onKeyUp={(e) => {
            if (e.key.toLocaleLowerCase() === 'enter') {
              form.handleSubmit();
            }
          }}
        />
      </Box>
      <Box component="div" className={classes.extraContainer}>
        <Box component="div" className={classes.checkboxContainer}>
          <Checkbox
            value={isRememberMe}
            onChange={(e) => setIsRememberMe(e.target.checked)}
            label="Remember Me"
            name="rememberMe"
            labelPlacement="end"
          />
        </Box>
        <Box component="div">
          <Link to="/forgot_password" data-cy="forgot-password-button" className={classes.link}>
            Forgot Password?
          </Link>
        </Box>
      </Box>
      <Box component="div">
        <Button
          color="primary"
          variant="contained"
          disableElevation
          fullWidth
          style={{ marginTop: '5px', fontSize: 16 }}
          className={classes.buttonCls}
          onClick={() => form.handleSubmit()}
          disabled={isLoading}
          data-cy="login-button"
        >
          {isLoading ? 'Logging In..' : 'Log In'}
        </Button>
      </Box>
      <div className={classes.loginFooter}>
        <span>New to casting.com? </span>
        <Button
          color="primary"
          variant="contained"
          disableElevation
          fullWidth
          style={{ marginTop: '20px' }}
          className={`${classes.registerBtn} ${classes.buttonCls}`}
          onClick={() => history.push('/signup')}
          disabled={isLoading}
          data-cy="button-register"
        >
          Register Now
        </Button>
      </div>
      <Box component="div"></Box>
      <Backdrop isLoading={isLoading} />
      <Box component="div" style={{ marginTop: 60, textAlign: 'center' }}>
        Questions or Concerns? Create a support ticket{' '}
        <a
          href="https://support.castingapp.com/support/home"
          target="_blank"
          style={{ color: 'white', fontWeight: 'bold', textDecoration: 'none' }}
        >
          here.
        </a>
      </Box>
    </FrontLayout>
  );
};

export default Login;
